<template>
  <div class="checkout-page">
    <div class="checkout-container">
      <!-- Header -->
      <div class="header">
        <button @click="goBack" class="back-button">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path d="M19 12H5M12 19l-7-7 7-7" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          Retour
        </button>
        <h1>Finaliser la commande</h1>
      </div>

      <!-- Form -->
      <form @submit.prevent="handleSubmit" class="checkout-form">
        <!-- Delivery Address -->
        <div class="section">
          <h2>Adresse de livraison</h2>
          <div class="form-group">
            <label>Rue *</label>
            <input
              v-model="form.deliveryAddress.street"
              type="text"
              class="input"
              placeholder="Numéro et nom de rue"
              required
            />
          </div>
          <div class="form-row">
            <div class="form-group">
              <label>Ville *</label>
              <input
                v-model="form.deliveryAddress.city"
                type="text"
                class="input"
                placeholder="Ville"
                required
              />
            </div>
            <div class="form-group">
              <label>Pays</label>
              <input
                v-model="form.deliveryAddress.country"
                type="text"
                class="input"
                placeholder="Pays"
              />
            </div>
          </div>
          <div class="form-group">
            <label>Instructions de livraison (optionnel)</label>
            <textarea
              v-model="form.deliveryAddress.instructions"
              class="textarea"
              placeholder="Appartement, code d'accès, repères..."
              rows="2"
            ></textarea>
          </div>
        </div>

        <!-- Payment Method -->
        <div class="section">
          <h2>Moyen de paiement</h2>
          <div class="payment-methods">
            <label
              v-for="method in paymentMethods"
              :key="method.id"
              class="payment-method"
              :class="{ active: form.paymentMethod === method.id }"
            >
              <input type="radio" v-model="form.paymentMethod" :value="method.id" />
              <div class="method-info">
                <span class="method-icon">{{ method.icon }}</span>
                <div>
                  <span class="method-name">{{ method.name }}</span>
                  <span class="method-description">{{ method.description }}</span>
                </div>
              </div>
            </label>
          </div>
        </div>

        <!-- Coupon Code -->
        <div class="section">
          <h2>Code promo (optionnel)</h2>
          <div class="coupon-input">
            <input
              v-model="form.couponCode"
              type="text"
              class="input"
              placeholder="Entrez votre code"
            />
            <button type="button" @click="applyCoupon" class="apply-button">
              Appliquer
            </button>
          </div>
          <p v-if="couponApplied" class="coupon-success">
            Code promo appliqué avec succès !
          </p>
        </div>

        <!-- Order Summary -->
        <div class="section order-summary">
          <h2>Récapitulatif</h2>
          <div class="summary-items">
            <div v-for="item in cartItems" :key="item.id" class="summary-item">
              <span class="item-quantity">{{ item.quantity }}x</span>
              <span class="item-name">{{ item.dishName }}</span>
              <span class="item-price">{{ formatPrice(item.totalPrice) }}</span>
            </div>
          </div>
          <div class="summary-totals">
            <div class="summary-row">
              <span>Sous-total</span>
              <span>{{ formatPrice(cartSubtotal) }}</span>
            </div>
            <div class="summary-row">
              <span>Frais de livraison</span>
              <div class="flex flex-col items-end text-right">
                <span
                  v-if="
                    $stores.cart.deliveryPromotion?.discountAmount > 0 &&
                    $stores.cart.originalDeliveryFee > $stores.cart.deliveryFee
                  "
                  class="text-xs text-gray-400 line-through"
                >
                  {{ formatPrice($stores.cart.originalDeliveryFee) }}
                </span>
                <span :class="{ 'text-green-600 font-bold': $stores.cart.deliveryPromotion?.discountAmount > 0 }">
                  {{ formatPrice($stores.cart.deliveryFee) }}
                  <span v-if="$stores.cart.deliveryPromotion?.discountAmount > 0" class="text-[10px] bg-green-100 text-green-600 px-1 rounded ml-1">
                    PROMO
                  </span>
                </span>
              </div>
            </div>
            <div class="summary-row">
              <span>Frais de service</span>
              <span>{{ formatPrice($stores.cart.serviceFee) }}</span>
            </div>
            <div v-if="$stores.cart.discount" class="summary-row discount">
              <span>Remise</span>
              <span>-{{ formatPrice($stores.cart.discount) }}</span>
            </div>
            <div class="summary-row total">
              <span>Total</span>
              <span>{{ formatPrice($stores.cart.total) }}</span>
            </div>
          </div>
        </div>

        <!-- Submit Button -->
        <button
          type="submit"
          class="submit-button"
          :disabled="$stores.orders.state.checkout.loading || !isFormValid"
        >
          <span v-if="$stores.orders.state.checkout.loading">
            <div class="button-spinner"></div>
            Traitement en cours...
          </span>
          <span v-else>Commander ({{ formatPrice(total) }})</span>
        </button>

        <!-- Error Message -->
        <div v-if="$stores.orders.state.checkout.error" class="error-message">
          {{ $stores.orders.state.checkout.error }}
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';

const route = useRoute();
const restaurantId = ref(route.params.restaurantId || route.query.restaurantId);

const form = ref({
  deliveryAddress: {
    street: '',
    city: '',
    country: 'Bénin',
    instructions: ''
  },
  paymentMethod: 'mobile_money',
  couponCode: ''
});

const paymentMethods = [
  // { id: 'cash', name: 'Espèces', description: 'Payez à la livraison', icon: '💵' },
  { id: 'mobile_money', name: 'Mobile Money', description: 'MTN, Moov, Orange', icon: '📱' },
  { id: 'card', name: 'Carte bancaire', description: 'Visa, Mastercard', icon: '💳' },
  { id: 'wallet', name: 'Portefeuille', description: 'Solde Rapidoo', icon: '👛' }
];

const cartItems = ref([]);
const couponApplied = ref(false);
const discount = ref(0);

// Mock cart data - in real app, get from cart store
// Remove mocked computed properties, use $stores.cart directly in template

const isFormValid = computed(() => {
  return form.value.deliveryAddress.street &&
         form.value.deliveryAddress.city &&
         form.value.paymentMethod &&
         cartItems.value.length > 0;
});

onMounted(() => {
  // Load cart items from store
  cartItems.value = window.$stores?.cart?.state?.cart?.items || [];

  // Load saved address from profile
  loadSavedAddress();
});

const loadSavedAddress = () => {
  const profile = window.$stores?.auth?.state?.user;
  if (profile?.address) {
    form.value.deliveryAddress = {
      ...form.value.deliveryAddress,
      street: profile.address.street || '',
      city: profile.address.city || '',
      country: profile.address.country || 'Bénin'
    };
  }
};

const applyCoupon = () => {
  if (form.value.couponCode) {
    // Validate coupon logic
    couponApplied.value = true;
    discount.value = 500; // Example discount
  }
};

const handleSubmit = () => {
  if (!isFormValid.value) return;

  window.$bus?.emit(window.$constants?.EVENTS?.ORDERS?.CREATE, {
    restaurantId: restaurantId.value,
    deliveryAddress: form.value.deliveryAddress,
    paymentMethod: form.value.paymentMethod,
    couponCode: form.value.couponCode || undefined
  });
};

const goBack = () => {
  window.$router?.back();
};

const formatPrice = (price) => {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'XOF',
    minimumFractionDigits: 0
  }).format(price);
};

// Listen for successful order creation
window.$bus?.on(window.$constants?.EVENTS?.ORDERS?.CREATED, (order) => {
  window.$router?.push(`/orders/${order.id}`);
});
</script>

<style scoped>
.checkout-page {
  min-height: 100vh;
  background: #f9fafb;
  padding: 20px;
}

.checkout-container {
  max-width: 600px;
  margin: 0 auto;
}

.header {
  margin-bottom: 24px;
}

.back-button {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0;
  background: none;
  border: none;
  color: #666;
  cursor: pointer;
  font-weight: 500;
  margin-bottom: 16px;
}

.header h1 {
  font-size: 1.5em;
  font-weight: bold;
  color: #1a1a1a;
}

.checkout-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.section {
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.section h2 {
  font-size: 1.2em;
  font-weight: 600;
  margin-bottom: 16px;
  color: #1a1a1a;
}

.form-group {
  margin-bottom: 16px;
}

.form-row {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 12px;
}

.form-group label {
  display: block;
  font-weight: 500;
  color: #374151;
  margin-bottom: 6px;
  font-size: 0.95em;
}

.input, .textarea {
  width: 100%;
  padding: 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 1em;
  font-family: inherit;
  transition: border-color 0.2s;
}

.input:focus, .textarea:focus {
  outline: none;
  border-color: #f59e0b;
}

.textarea {
  resize: vertical;
}

.payment-methods {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.payment-method {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
}

.payment-method:hover {
  border-color: #f59e0b;
}

.payment-method.active {
  border-color: #f59e0b;
  background: #fffbeb;
}

.payment-method input[type="radio"] {
  width: 18px;
  height: 18px;
  accent-color: #f59e0b;
}

.method-info {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
}

.method-icon {
  font-size: 1.5em;
}

.method-name {
  font-weight: 600;
  color: #1a1a1a;
  display: block;
}

.method-description {
  font-size: 0.85em;
  color: #666;
}

.coupon-input {
  display: flex;
  gap: 8px;
}

.coupon-input .input {
  flex: 1;
}

.apply-button {
  padding: 12px 20px;
  background: #374151;
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
}

.coupon-success {
  margin-top: 8px;
  color: #10b981;
  font-size: 0.9em;
}

.summary-items {
  border-bottom: 2px solid #f3f4f6;
  padding-bottom: 16px;
  margin-bottom: 16px;
}

.summary-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
}

.item-quantity {
  font-weight: 600;
  color: #f59e0b;
  min-width: 30px;
}

.item-name {
  flex: 1;
  margin-left: 12px;
  color: #374151;
}

.item-price {
  font-weight: 600;
  color: #1a1a1a;
}

.summary-totals {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  color: #374151;
}

.summary-row.total {
  font-weight: 700;
  font-size: 1.2em;
  color: #1a1a1a;
  border-top: 2px solid #f3f4f6;
  margin-top: 8px;
  padding-top: 16px;
}

.summary-row.discount {
  color: #10b981;
}

.submit-button {
  width: 100%;
  padding: 16px;
  background: #f59e0b;
  color: white;
  border: none;
  border-radius: 12px;
  font-weight: 600;
  font-size: 1.1em;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: background 0.2s;
}

.submit-button:hover:not(:disabled) {
  background: #e5940b;
}

.submit-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.button-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid #ffffff;
  border-top-color: transparent;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.error-message {
  background: #fee2e2;
  color: #991b1b;
  padding: 12px;
  border-radius: 8px;
  text-align: center;
}
</style>
